/**
 * Execution History Modal Component
 *
 * A modal dialog for viewing and selecting past workflow executions.
 * Shows execution overview when an execution is selected.
 */

import React, { useEffect } from 'react';
import { X, History } from 'lucide-react';
import { useI18n } from '@/i18n';
import { ExecutionHistoryList } from './ExecutionHistoryList';
import { ExecutionOverview } from './ExecutionOverview';
import { useExecution } from '../hooks/useImageWorkflows';
import {
  useWorkflowStore,
  useSelectedExecutionId,
  useIsViewingHistory,
} from '../hooks/useWorkflowStore';

interface ExecutionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string | null;
  /** Callback when user clicks resume on a failed execution */
  onResume?: (executionId: string, userRunId: string, resumeNodeKey: string) => void;
  /** Whether resume is in progress */
  isResuming?: boolean;
}

export const ExecutionHistoryModal: React.FC<ExecutionHistoryModalProps> = ({
  isOpen,
  onClose,
  workflowId,
  onResume,
  isResuming = false,
}) => {
  const { messages } = useI18n();
  const t = messages.imageWorkflows;

  const clearHistoryView = useWorkflowStore((s) => s.clearHistoryView);
  const selectedExecutionId = useSelectedExecutionId();
  const isViewingHistory = useIsViewingHistory();

  // Fetch selected execution details
  const { data: selectedExecution, isLoading: isLoadingExecution } = useExecution(
    selectedExecutionId,
    { includeRuns: true }
  );

  // Clear history view when modal closes
  useEffect(() => {
    if (!isOpen && isViewingHistory) {
      clearHistoryView();
    }
  }, [isOpen, isViewingHistory, clearHistoryView]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-10 lg:inset-16 bg-white rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <History className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t?.executionHistory?.title || 'Execution History'}
              </h2>
              <p className="text-sm text-gray-500">
                View past executions and their results
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Execution list */}
          <div className="w-80 border-r overflow-y-auto p-4 bg-white">
            <ExecutionHistoryList workflowId={workflowId} />
          </div>

          {/* Right: Selected execution overview */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {isLoadingExecution ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2" />
                  <p className="text-gray-500">Loading execution details...</p>
                </div>
              </div>
            ) : selectedExecution ? (
              <ExecutionOverview
                execution={selectedExecution}
                onResume={onResume}
                isResuming={isResuming}
              />
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <History className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium mb-2">
                    Select an execution
                  </p>
                  <p className="text-sm max-w-xs mx-auto">
                    Click on an execution from the list to view its details.
                    The results will also be displayed on the workflow canvas.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50">
          <p className="text-xs text-gray-500">
            {selectedExecution
              ? 'Execution results are displayed on the workflow canvas'
              : 'Select an execution to view details and apply to canvas'}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors"
          >
            {messages.common?.close || 'Close'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ExecutionHistoryModal;
